import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { gaeList } from '../requestedDocuments/ga';
import { confirmDialog } from '../helpers/dialogUtils';
import II18 from '../translate/II18';
import {
  removeDocumentPage,
  setCameraMode,
  setDocumentDataUrl,
  updateDocumentPagesOrder
} from '../../redux/actions/rootAction';
import withDocumentCommon from '../helpers/withDocumentCommon';
import withDesktopUpload from '../helpers/withDesktopUpload';
import CrossDevicePopUp from '../common/CrossDevicePopUp';
import { gaeApp } from '../helpers/Analytics';
import withCrossDeviceDocument from '../helpers/withCrossDeviceDocument';
import { isRequestACrossDeviceDocument } from '../../lib/utils';

export default function withGalleryManager(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class withGalleryWrapper extends React.Component {
    constructor(props) {
      super(props);
      // noinspection JSUnusedLocalSymbols
      this.state = {
        selectMode: false,
        selected: new Set()
      };
    }

    onSwitchSelectMode = () => {
      this.resetSelection();
      this.setState(prevState => ({
        selectMode: !prevState.selectMode
      }));
    };

    selectItem = key => {
      const selected = new Set(this.state.selected);
      if (selected.has(key)) {
        selected.delete(key);
      } else {
        selected.add(key);
      }
      this.setState({ selected: selected });
    };

    onSelectAllClick = () => {
      const selected = new Set();
      for (let [key] of this.props.scannedPages) {
        if (!this.props.successSentKeys.has(this.props.document.id + key)) {
          selected.add(key);
        }
      }

      this.setState({ selected: selected });
    };

    deleteItem = key => {
      gaeList('DELETE_SELECTED_PAGE', key);
      this.props.removeDocumentPage(this.props.document.id, key);
      this.forceUpdate();
    };

    replaceItem = key => {
      gaeList('REPLACE_SELECTED_PAGE', key);
      this.props.replaceDocumentPage(this.props.document.id, key);
    };

    resetSelection = () => {
      this.setState({ selected: new Set() });
    };

    componentDidUpdate(prevProps, prevState) {
      if (prevProps.scannedPages !== this.props.scannedPages) {
        this.setState({
          selected: new Set(),
          selectMode: false
        });
      }
    }

    onDeleteAllClick = () => {
      gaeList('DIALOG_DELETE_ALL_PAGES', 'SHOW', true);

      const requestedDocumentId = this.props.document.id;
      const scannedPages = this.props.scannedPages;
      const selected = new Set();

      confirmDialog(
        '',
        II18.get('dialog_delete_all'),
        () => {
          for (let [key, url] of scannedPages) {
            URL.revokeObjectURL(url);
            selected.add(key);
          }
          gaeList('DIALOG_DELETE_ALL_PAGES', 'CLOSE', true);
          gaeList('DELETE_ALL_DOCUMENT_PAGES', requestedDocumentId);
          this.props.removeDocumentPage(requestedDocumentId, selected);
          this.forceUpdate();
        },
        () => {
          gaeList('DIALOG_DELETE_ALL_PAGES', 'CLOSE', true);
        }
      );
    };

    onDeleteSelectedClick = () => {
      const requestedDocumentId = this.props.document.id;
      const scannedPages = this.props.scannedPages;
      const selected = this.state.selected;

      if (selected.size < 1) return;

      gaeList('DIALOG_DELETE_SELECTED_PAGES', 'SHOW', true);

      confirmDialog(
        '',
        II18.get(
          selected.size > 1 ? 'dialog_delete_pages' : 'dialog_delete_page'
        ),
        () => {
          for (let id of selected) {
            URL.revokeObjectURL(scannedPages.get(id));
          }
          gaeList('DIALOG_DELETE_SELECTED_PAGES', 'CLOSE', true);
          gaeList('DELETE_SELECTED_DOCUMENT_PAGES', requestedDocumentId);
          this.props.removeDocumentPage(requestedDocumentId, selected);
          this.forceUpdate();
        },
        () => {
          gaeList('DIALOG_DELETE_SELECTED_PAGES', 'CLOSE', true);
        }
      );
    };

    uploadToGallery = () => {
      this.props.loadFromFile(this.props.document.id);
    };

    allowDeleteAllPages = () => {
      let hasSubmittedImages = false;
      for (let [key] of this.props.scannedPages) {
        hasSubmittedImages =
          hasSubmittedImages ||
          this.props.successSentKeys.has(this.props.document?.id + key);
      }
      return !hasSubmittedImages;
    };

    saveGalleryToExternal = () => {};

    continueGalleryOnMobile = () => {
      this.setState({ showGalleryOnMobilePopup: true });
    };

    onGalleryOnMobileClosed = () => {
      this.setState({ showGalleryOnMobilePopup: false });
    };

    onGalleryOnMobileDone = (
      pages,
      documentOptions,
      downloadUrl,
      onApplyExternalDataDone
    ) => {
      setTimeout(
        () =>
          this.applyCrossDeviceDataToGallery(
            pages,
            documentOptions,
            downloadUrl
          ).then(() => {
            onApplyExternalDataDone();
            this.setState(
              {
                crossDeviceDocumentFinished: true
              },
              () =>
                setTimeout(() => {
                  this.setState({
                    showGalleryOnMobilePopup: false
                  });
                }, 3000)
            );
          }),
        3000
      );
    };

    applyCrossDeviceDataToGallery = async (
      pages,
      documentOptions,
      downloadUrl
    ) => {
      gaeApp('GALLERY.SAVE_CROSS_DEVICE_DATA_LOCAL');

      return this.props.downloadImagesFromCrossDeviceDocumentsStorage(
        pages,
        this.props.document.id,
        downloadUrl
      );
    };

    render() {
      return (
        <>
          {window.isDesktop && (
            <CrossDevicePopUp
              templateMode={this.props.templateMode}
              opened={this.state.showGalleryOnMobilePopup}
              onClose={this.onGalleryOnMobileClosed}
              onDone={this.onGalleryOnMobileDone}
              onCrossDeviceCreated={putUrl =>
                this.saveGalleryToExternal(putUrl)
              }
              recipientRequestId={this.props.recipientRequestId}
              docId={this.props.document.id}
            />
          )}
          <WrappedComponent
            noDeleteButton={!this.allowDeleteAllPages()}
            onSwitchSelectMode={this.onSwitchSelectMode}
            selectMode={this.state.selectMode}
            selected={this.state.selected}
            onSelectAllClick={this.onSelectAllClick}
            onSelectItem={this.selectItem}
            onDeleteItem={this.deleteItem}
            onReplaceItem={this.replaceItem}
            onResetSelection={this.resetSelection}
            onAddNewItem={this.uploadToGallery}
            onDeleteSelectedClick={this.onDeleteSelectedClick}
            onDeleteAllClick={this.onDeleteAllClick}
            onContinueGalleryOnMobile={this.continueGalleryOnMobile}
            images={this.props.scannedPages}
            {...this.props}
            document={
              this.props.document || {
                id: 'fake_document'
              }
            }
          />
        </>
      );
    }
  }

  withGalleryWrapper.propTypes = {
    embedMode: PropTypes.bool,
    templateMode: PropTypes.bool,
    uploadImagesToCrossDeviceDocumentsStorage: PropTypes.func,
    downloadImagesFromCrossDeviceDocumentsStorage: PropTypes.func,
    recipientRequestId: PropTypes.string,
    removeDocumentPage: PropTypes.func,
    loadFromFile: PropTypes.func,
    replaceDocumentPage: PropTypes.func,
    scannedPages: PropTypes.object,
    document: PropTypes.object,
    successSentKeys: PropTypes.object
  };

  const mapStateToProps = (state, ownProps) => {
    return {
      embedMode: state.embedMode,
      templateMode: state.templateMode,
      executeOneDocumentOnMobile: isRequestACrossDeviceDocument(
        state.requestStartInfo
      ),
      recipientRequestId: state.recipientRequestId,
      successSentKeys: state.successSentKeys,
      errorSentKeys: state.errorSentKeys,
      scrollDown: ownProps.scrollDown,
      cameraMode: state.cameraMode,
      doNotModifyCameraMode: ownProps.doNotModifyCameraMode
    };
  };

  const mapDispatchToProps = dispatch => ({
    setCameraMode: as => dispatch(setCameraMode(as)),
    setDocumentDataUrl: (documentId, pageId, dataUrl) =>
      dispatch(setDocumentDataUrl(documentId, pageId, dataUrl)),
    updateDocumentPagesOrder: (documentId, oldIndex, newIndex) =>
      dispatch(updateDocumentPagesOrder(documentId, oldIndex, newIndex)),
    removeDocumentPage: (documentId, pageId) =>
      dispatch(removeDocumentPage(documentId, pageId))
  });

  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    withDocumentCommon(
      withDesktopUpload(withCrossDeviceDocument(withGalleryWrapper))
    )
  );
}
